/**
 * SA-1328 (spec §4): the band's landing after a task is done. Once per mount,
 * on the first render with `active && ready && target`, it rings the target,
 * tells the parent (which clears its flag), and scrolls the target to the
 * middle of the space under the sticky strip — now, and again on every
 * resize of the page frame (the band above loads after this one and moves
 * things) — until the person moves the page themselves or `FOCUS_FOLLOW_MS`
 * passes. The ring clears on its own timer. Only an unmount stops either.
 */
import { useEffect, useRef, useState, type RefObject } from 'react'
import { landingScrollTop, type LandingTarget } from './bandLanding'
import { FOCUS_FOLLOW_MS } from './RecordSection'

const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const

interface Opts {
  active: boolean
  ready: boolean
  target: LandingTarget | null
  rootRef: RefObject<HTMLElement | null>
  onLanded?: () => void
}

function elementFor(root: HTMLElement, target: LandingTarget): HTMLElement | null {
  if (target.kind === 'step') return root.querySelector<HTMLElement>(`[data-step-id="${CSS.escape(target.stepId)}"][data-current="true"]`)
  if (target.kind === 'row') return root.querySelector<HTMLElement>(`[data-timeline-key="${CSS.escape(target.key)}"]`)
  return root
}

export function useBandLanding({ active, ready, target, rootRef, onLanded }: Opts): { ringStep: string | null; ringKey: string | null } {
  const [ring, setRing] = useState<LandingTarget | null>(null)
  const started = useRef(false)
  const onLandedRef = useRef(onLanded)
  onLandedRef.current = onLanded
  const stops = useRef<Array<() => void>>([])

  useEffect(() => {
    if (started.current || !active || !ready || !target) return
    const root = rootRef.current
    if (!root) return
    started.current = true
    setRing(target)
    onLandedRef.current?.()
    let stopped = false
    const scroll = () => {
      if (stopped) return
      const el = elementFor(root, target)
      if (!el) return
      const scroller = root.closest('main')
      if (!scroller) {
        el.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
        return
      }
      const strip = scroller.querySelector<HTMLElement>('[data-slot="sticky-page-header"]')
      const scrollerRect = scroller.getBoundingClientRect()
      const box = el.getBoundingClientRect()
      scroller.scrollTo({
        top: landingScrollTop({
          scrollTop: scroller.scrollTop, scrollerTop: scrollerRect.top, scrollerHeight: scroller.clientHeight || scrollerRect.height,
          stripBottom: strip ? strip.getBoundingClientRect().bottom : null,
          targetTop: box.top, targetHeight: box.height, align: target.kind === 'band' ? 'start' : 'center',
        }),
        behavior: 'smooth',
      })
    }
    scroll()
    const frame = root.closest('[data-slot="page-frame"]') ?? root.parentElement
    const observer = typeof ResizeObserver !== 'undefined' && frame ? new ResizeObserver(() => scroll()) : null
    observer?.observe(frame as Element)
    const stopFollow = () => {
      stopped = true
      observer?.disconnect()
      clearTimeout(followTimer)
      for (const type of USER_SCROLL_EVENTS) window.removeEventListener(type, stopFollow)
    }
    const followTimer = setTimeout(stopFollow, FOCUS_FOLLOW_MS)
    for (const type of USER_SCROLL_EVENTS) window.addEventListener(type, stopFollow, { passive: true })
    const ringTimer = setTimeout(() => setRing(null), FOCUS_FOLLOW_MS)
    stops.current.push(stopFollow, () => clearTimeout(ringTimer))
  }, [active, ready, target, rootRef])

  // Only an unmount stops the follow and the ring. The guard resets too, so
  // StrictMode's simulated remount lands again instead of leaving a stopped
  // follow and a ring nothing clears.
  useEffect(() => () => {
    for (const stop of stops.current) stop()
    stops.current = []
    started.current = false
  }, [])

  return { ringStep: ring?.kind === 'step' ? ring.stepId : null, ringKey: ring?.kind === 'row' ? ring.key : null }
}
